/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MdLockReset } from 'react-icons/md';
import { format, parseISO } from 'date-fns';
import Swal from 'sweetalert2';
import { IoLogInOutline } from 'react-icons/io5';
import { TableList, Container, Filter } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import { IOption } from '~/components/Select';
import Loading from '~/components/Loading';

interface IOrder {
  processing_fee: string;
  service_fee: string;
  total: string;
}

interface IUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  additionalInformation: {
    gender: string;
    verified: boolean;
    document?: string;
    phone: string;
    birthdate: string;
    address: {
      zip_code: string;
      street: string;
      number: number;
      neighborhood: string;
      city: string;
      state: string;
      country: string;
      complement?: string;
    };
  };
  orders: IOrder[];
}

interface IUserResponse {
  data: IUser[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IDataItem {
  id: number;
  name: string;
  email: string;
  phone: string;
  last_move: string;
  city: string;
  state: string;
  registration_date: string;
  events: boolean;
  profiles: boolean;
  active: boolean | string;
}

interface IStatus {
  id: number;
  name: string;
}

interface IOrganizer {
  id: number;
  name: string;
}

const Users: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [status, setStatus] = useState<IOption[]>([]);
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [userCity, setUserCity] = useState<string | undefined>(undefined);
  const [userState, setUserState] = useState<string | undefined>(undefined);
  const [statusId, setStatusId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [organizerId, setOrganizerId] = useState<string | undefined>(undefined);
  const [documentTicket, setDocumentTicket] = useState<string | undefined>();

  // subir

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const dataStatus = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(dataStatus);
    });

    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const handleLoadUsers = useCallback(
    async (
      pageData,
      name?: string,
      email?: string,
      city?: string,
      state?: string,
      status_id?: string,
      quantityData?: number,
      organizer_id?: string,
      documentData?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IUserResponse>(`users`, {
          params: {
            page: pageData,
            name,
            email,
            city,
            state,
            status_id,
            quantity: quantityData,
            organizer_id,
            document: documentData,
          },
        })
        .finally(() => setLoading(false));

      const dataAux = response.data.data.map<IDataItem>((userData) => {
        const total = userData.orders.reduce((previous, current) => {
          return (
            previous +
            (parseFloat(current.total) -
              parseFloat(current.processing_fee) -
              parseFloat(current.service_fee))
          );
        }, 0);
        return {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          document: userData.additionalInformation?.document || 'N/A',
          gender: userData.additionalInformation?.gender || 'N/A',
          birthdate: userData.additionalInformation?.birthdate
            ? format(
                parseISO(userData.additionalInformation.birthdate),
                'dd/MM/yyyy'
              )
            : 'N/A',
          phone: userData.additionalInformation?.phone || 'N/A',
          last_move: formatPrice(total),
          city: userData.additionalInformation?.address?.city || 'N/A',
          state: userData.additionalInformation?.address?.state || 'N/A',
          registration_date: format(
            parseISO(userData.created_at),
            'dd/MM/yyyy'
          ),
          events: true,
          profiles: true,
          active: userData.additionalInformation?.verified,
        };
      });

      setData(dataAux);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    []
  );

  useEffect(() => {
    handleLoadUsers(1);
  }, [handleLoadUsers]);

  const handleToggleVerified = useCallback(
    async (userId: number, currentValue: boolean) => {
      const newValue = !currentValue;
      await api.patch(`additional-informations/${userId}`, {
        verified: newValue,
      });
      setData((oldState) =>
        oldState.map((item) =>
          item.id === userId ? { ...item, active: newValue } : item
        )
      );
    },
    []
  );

  const handleClickResetPassword = useCallback((userId: number) => {
    Swal.fire({
      title: 'Tem certeza?',
      html: 'Deseja resetar a senha deste usuário?<br>Ele receberá um e-mail com uma nova senha.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#b34a00',
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        api
          .patch(`users/${userId}/reset-password`)
          .then(() => {
            Swal.fire({
              title: 'Senha resetada!',
              text: 'A senha do usuário foi resetada e um e-mail foi enviado.',
              icon: 'success',
              confirmButtonColor: '#ff6900',
            });
          })
          .catch(() => {
            Swal.fire({
              title: 'Erro!',
              text: 'Ocorreu um erro ao resetar a senha. Tente novamente.',
              icon: 'error',
              confirmButtonColor: '#ff6900',
            });
          });
      }
    });
  }, []);

  const handleClickLogin = useCallback((user: IUser) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Gostaria de logar como o(a) atleta ${user.name}?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        const response = await api.post(`users/sessions/set-login/${user.id}`, {
          type: 'Atleta',
        });
        window.open(
          `${process.env.REACT_APP_ATHLETE_URL}/auto-login/${response.data.token}`,
          '_blank'
        );
      }
    });
  }, []);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: '#',
        selector: 'id',
        sortable: true,
      },
      {
        name: 'Nome',
        selector: 'name',
        sortable: true,
      },
      {
        name: 'E-mail',
        selector: 'email',
        sortable: true,
      },
      {
        name: 'Verificado',
        selector: 'active',
        sortable: true,
        cell: (row) => (
          <div className="form-check form-switch ms-4">
            <input
              className="form-check-input"
              type="checkbox"
              checked={row.active}
              onChange={() => handleToggleVerified(row.id, row.active)}
            />
          </div>
        ),
      },
      {
        name: 'Documento',
        selector: 'document',
        sortable: true,
      },
      {
        name: 'Gênero',
        selector: 'gender',
        sortable: true,
      },
      {
        name: 'Nascimento',
        selector: 'birthdate',
        sortable: true,
      },
      {
        name: 'Fone',
        selector: 'phone',
        sortable: true,
      },
      {
        name: 'Movimentação',
        selector: 'last_move',
        sortable: true,
      },
      {
        name: 'Cidade',
        selector: 'city',
        sortable: true,
      },
      {
        name: 'Estado',
        selector: 'state',
        sortable: true,
      },
      {
        name: 'Data Cadastro',
        selector: 'registration_date',
        sortable: true,
      },
      {
        name: 'Resetar senha',
        selector: 'reset_password',
        sortable: false,
        cell: (row) => (
          <button
            type="button"
            className="btn-icons border-0"
            onClick={() => handleClickResetPassword(row.id)}
          >
            <MdLockReset size={20} color="#fff" />
          </button>
        ),
      },
      {
        name: 'Logar como',
        selector: 'login_as',
        sortable: false,
        cell: (row) => (
          <button
            type="button"
            className="btn-icons border-0"
            onClick={() => handleClickLogin(row)}
          >
            <IoLogInOutline size={20} color="#fff" />
          </button>
        ),
      },
      // {
      //   name: 'Perfis',
      //   selector: 'profiles',
      //   sortable: false,
      //   cell: () => (
      //     <button type="button" className="btn-icons border-0">
      //       <MdCheck size={16} color="#fff" />
      //     </button>
      //   ),
      // },
    ],
    [handleToggleVerified, handleClickResetPassword]
  );

  const handleChangePage = useCallback(
    (page) => {
      handleLoadUsers(
        page,
        userName,
        userEmail,
        userCity,
        userState,
        statusId,
        quantity,
        organizerId,
        documentTicket
      );
      setPageSelected(page);
    },
    [
      handleLoadUsers,
      userName,
      userEmail,
      userCity,
      userState,
      statusId,
      quantity,
      organizerId,
      documentTicket,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      console.log(formData);
      setUserName(formData.name);
      setUserEmail(formData.email);
      setUserCity(formData.city);
      setUserState(formData.state);
      setStatusId(formData.status !== 'todos' ? formData.status : undefined);
      setOrganizerId(
        formData.organizer !== 'todos' ? formData.organizer : undefined
      );
      setDocumentTicket(formData.document || undefined);
      handleLoadUsers(
        1,
        formData.name,
        formData.email,
        formData.city,
        formData.state,
        formData.status !== 'todos' ? formData.status : undefined,
        quantity,
        formData.organizer !== 'todos' ? formData.organizer : undefined,
        formData.document || undefined
      );
    },
    [handleLoadUsers, quantity]
  );

  const handleClickExportCsv = useCallback(async () => {
    setLoading(true);
    const response = await api
      .get<IUser[]>('users', {
        params: {
          name: userName,
          email: userEmail,
          city: userCity,
          state: userState,
          status_id: statusId,
          document: documentTicket,
        },
      })
      .finally(() => setLoading(false));

    const dataAux = response.data.map<IDataItem>((userData) => {
      const total = userData.orders.reduce((previous, current) => {
        return (
          previous +
          (parseFloat(current.total) -
            parseFloat(current.processing_fee) -
            parseFloat(current.service_fee))
        );
      }, 0);
      return {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        document: userData.additionalInformation?.document || 'N/A',
        gender: userData.additionalInformation?.gender || 'N/A',
        birthdate: userData.additionalInformation?.birthdate
          ? format(
              parseISO(userData.additionalInformation.birthdate),
              'dd/MM/yyyy'
            )
          : 'N/A',
        phone: userData.additionalInformation?.phone || 'N/A',
        last_move: total.toFixed(2).replace('.', ',').toString(),
        city: userData.additionalInformation?.address?.city || 'N/A',
        state: userData.additionalInformation?.address?.state || 'N/A',
        registration_date: format(parseISO(userData.created_at), 'dd/MM/yyyy'),
        events: true,
        profiles: true,
        active: userData.additionalInformation?.verified ? 'Sim' : 'Não',
      };
    });

    return dataAux;
  }, [documentTicket, statusId, userCity, userEmail, userName, userState]);

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      handleLoadUsers(
        1,
        userName,
        userEmail,
        userCity,
        userState,
        statusId,
        quantityData,
        organizerId,
        documentTicket
      );
      setQuantity(quantityData);
      setPageSelected(1);
    },
    [
      handleLoadUsers,
      userName,
      userEmail,
      userCity,
      userState,
      statusId,
      organizerId,
      documentTicket,
    ]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de usuários</h1>

              <ExportCsv
                data={data}
                columns={columns}
                fileName="Lista_de_usuarios"
                onClick={handleClickExportCsv}
              />
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              document
              name
              nameUser
              email
              city
              state
              statusOptions={status}
              organizer
              organizersData={organizers}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        changeQuantity
                        showTotals
                        generalTotal={tableData.generalTotal}
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        onChangeQuantity={handleChangeQuantity}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Users;
